<template>
  <b-card no-body class="filter-panel-v2">

    <!-- ── Mobile Top Bar (< lg) — search opens full-screen keyword sheet; Sliders opens the inline advanced-filter panel ── -->
    <div class="d-lg-none filter-topbar px-1 py-75">
      <div class="d-flex align-items-center" style="gap: 0.5rem;">
        <button type="button" class="form-control search-trigger flex-grow-1 d-flex align-items-center" @click="openKeywordSheet">
          <feather-icon icon="SearchIcon" size="14" class="mr-50 text-muted flex-shrink-0" />
          <span class="text-truncate" :class="{ 'text-muted': !form.keyword }">{{ form.keyword || 'code, name, owner, building, unit/house no, floor...' }}</span>
        </button>
        <b-button variant="outline-secondary" size="sm" class="filter-toggle-btn" @click="collapsed = !collapsed">
          <feather-icon :icon="collapsed ? 'SlidersIcon' : 'ChevronUpIcon'" size="14" />
        </b-button>
      </div>
    </div>

    <!-- ── Mobile keyword search: full-screen slide-up sheet ── -->
    <transition name="keyword-sheet-anim">
      <div v-if="keywordSheetOpen" class="d-lg-none keyword-sheet" @touchmove.passive="blurKeywordInput">
        <div class="sheet-header-mobile">
          <button type="button" class="sheet-back-btn" aria-label="Close search" @click="closeKeywordSheet">
            <feather-icon icon="ArrowLeftIcon" size="20" />
          </button>
          <div class="position-relative flex-grow-1">
            <b-input-group>
              <input
                ref="sheetKeywordInput"
                v-model="form.keyword"
                type="text"
                class="form-control"
                :style="form.keyword ? 'border-right: none !important;' : ''"
                placeholder="code, name, owner, building, unit/house no, floor..."
                autocomplete="off"
                @input="onKeywordInput"
                @paste="onKeywordPaste"
                @keydown.down.prevent="kwIndex = Math.min(kwIndex + 1, kwSuggestions.length - 1)"
                @keydown.up.prevent="kwIndex = Math.max(kwIndex - 1, 0)"
                @keydown.enter.prevent="onKeywordEnter"
                @blur="hideKwDropdown"
              />
              <b-input-group-append v-if="form.keyword">
                <span
                  class="input-group-text kw-clear-wrap"
                  @mousedown.prevent="form.keyword = ''; kwSuggestions = []; showKwSuggestions = false; submit()"
                >
                  <span class="kw-clear-circle"><feather-icon icon="XIcon" size="10" /></span>
                </span>
              </b-input-group-append>
              <b-input-group-append>
                <b-button variant="primary" @click="submitKeyword">
                  <feather-icon icon="SearchIcon" size="14" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>
        </div>
        <div class="small text-muted px-1 pt-50 pb-25" style="line-height: 1.3;">
          To filter by building or unit type, type <strong>building</strong> or <strong>type</strong> first, then the value (e.g. "building A")
        </div>
        <div class="keyword-sheet-body px-1" @scroll="blurKeywordInput" @touchmove.passive="blurKeywordInput">
          <ul v-if="showKwSuggestions && kwSuggestions.length" class="list-group sheet-kw-suggestions">
            <li v-for="(item, i) in kwSuggestions" :key="'m-kw-' + i" class="list-group-item list-group-item-action d-flex align-items-center py-1" :class="{ active: i === kwIndex }" @mousedown.prevent="selectKwSuggestion(i)">
              <span class="kw-badge mr-2" :class="'kw-' + item.type">{{ { code: 'Code', place: 'Property', owner: 'Owner', building: 'Building', transit: 'Station', zone: 'Zone', unit_type: 'Type', floor: 'Floor', unit_no: 'Unit No', house_no: 'House No' }[item.type] || item.type }}</span>
              <span class="text-truncate">{{ item.label }}</span>
            </li>
          </ul>
        </div>
      </div>
    </transition>

    <!-- ── Desktop Top Bar (>= lg) ── -->
    <div class="d-none d-lg-block filter-topbar px-1 pt-75 pb-50">

      <!-- Action row -->
      <div class="d-flex justify-content-between align-items-center mb-75">
        <span class="font-weight-bold" style="font-size: 0.95rem; color: #5e5873;">Quick Search</span>
        <div class="d-flex align-items-center" style="gap: 0.5rem;">
          <b-dropdown v-if="templateData.length" variant="outline-secondary" text="Templates" size="sm" right>
            <b-dropdown-item
              v-for="t in templateData"
              :key="t.id"
              link-class="d-flex align-items-center pr-2"
              style="min-width: 180px;"
              @click="loadTemplate(t)"
            >
              <span class="flex-grow-1 mr-3">{{ t.title }}</span>
              <feather-icon icon="Trash2Icon" size="13" class="text-danger cursor-pointer flex-shrink-0" @click.stop="deleteTemplate(t.id)" />
            </b-dropdown-item>
          </b-dropdown>
          <b-button variant="primary" size="sm" :to="{ name: 'property-create' }">
            <feather-icon icon="PlusIcon" size="14" class="mr-25" />New Property
          </b-button>
        </div>
      </div>

      <!-- Quick search row -->
      <div class="d-flex align-items-flex-end flex-wrap" style="gap: 0.5rem; align-items: flex-end;">

        <!-- Code / Name -->
        <div class="topbar-field-code">
          <label class="topbar-input-label">Code / Name</label>
          <div class="position-relative">
            <input
              v-model="form.propertyfilter"
              type="text"
              class="form-control topbar-clearable"
              placeholder="code, name..."
              autocomplete="off"
              @input="onPropFilterInput"
              @keydown.down.prevent="placeIndex = Math.min(placeIndex + 1, filteredPlaces.length - 1)"
              @keydown.up.prevent="placeIndex = Math.max(placeIndex - 1, 0)"
              @keydown.enter.prevent="placeIndex >= 0 ? selectPlaceFromFilter(placeIndex) : submit()"
              @blur="hidePropFilterSugg"
            />
            <span v-if="form.propertyfilter" class="topbar-clear-btn" @mousedown.prevent="form.propertyfilter = ''; form.place_id = null; placeSearch = ''; filteredPlaces = []; showPlaceSugg = false; debouncedSubmit()"><feather-icon icon="XIcon" size="10" /></span>
            <ul v-if="showPlaceSugg && filteredPlaces.length" class="list-group position-absolute zindex-dropdown shadow-sm" style="top: 100%; left: 0; min-width: 100%; width: max-content; max-width: 320px; max-height: 200px; overflow-y: auto;">
              <li
                v-for="(item, i) in filteredPlaces"
                :key="'d-cn-' + item.type + '-' + (item.id || item.value)"
                class="list-group-item list-group-item-action d-flex align-items-center py-1"
                :class="{ active: i === placeIndex }"
                @mousedown.prevent="selectPlaceFromFilter(i)"
              >
                <span class="kw-badge mr-2" :class="'kw-' + item.type">{{ item.type }}</span>
                <span class="text-truncate">{{ item.type === 'place' && item.name && item.name[$i18n.locale] ? item.name[$i18n.locale] : item.label }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Owner -->
        <div class="position-relative topbar-field-owner">
          <label class="topbar-input-label">Owner</label>
          <div class="position-relative">
            <input v-model="ownerSearch" type="text" class="form-control topbar-clearable" placeholder="name / phone..." autocomplete="off" @input="onOwnerInput" @keydown.down.prevent="ownerIndex = Math.min(ownerIndex + 1, filteredOwners.length - 1)" @keydown.up.prevent="ownerIndex = Math.max(ownerIndex - 1, 0)" @keydown.enter.prevent="selectOwner(ownerIndex)" @blur="hideOwnerDrop" @keyup.enter="filteredOwners.length === 0 && submit()" />
            <span v-if="ownerSearch" class="topbar-clear-btn" @mousedown.prevent="ownerSearch = ''; form.owner_id = null; debouncedSubmit()"><feather-icon icon="XIcon" size="10" /></span>
          </div>
          <ul v-if="showOwnerSugg && filteredOwners.length" class="list-group position-absolute zindex-dropdown shadow-sm" style="top: 100%; left: 0; width: 100%; max-height: 200px; overflow-y: auto;">
            <li v-for="(item, i) in filteredOwners" :key="'d-own-' + item.id + '-' + i" class="list-group-item list-group-item-action py-1" :class="{ active: i === ownerIndex }" @mousedown.prevent="selectOwner(i)">{{ item.firstname }} {{ item.lastname }}</li>
          </ul>
        </div>

        <!-- Unit / House -->
        <div class="topbar-field-sm">
          <label class="topbar-input-label">Unit / House No.</label>
          <div class="position-relative">
            <input v-model="form.unit" type="text" class="form-control topbar-clearable" placeholder="no." autocomplete="off" @input="debouncedSubmit" @keyup.enter="submit" />
            <span v-if="form.unit" class="topbar-clear-btn" @mousedown.prevent="form.unit = ''; debouncedSubmit()"><feather-icon icon="XIcon" size="10" /></span>
          </div>
        </div>

        <!-- Floor -->
        <div class="topbar-field-sm">
          <label class="topbar-input-label">Floor</label>
          <div class="position-relative">
            <input v-model="form.floor" type="text" class="form-control topbar-clearable" placeholder="floor" autocomplete="off" @input="debouncedSubmit" @keyup.enter="submit" />
            <span v-if="form.floor" class="topbar-clear-btn" @mousedown.prevent="form.floor = ''; debouncedSubmit()"><feather-icon icon="XIcon" size="10" /></span>
          </div>
        </div>

        <!-- Building -->
        <div class="topbar-field-sm">
          <label class="topbar-input-label">Building</label>
          <div class="position-relative">
            <input v-model="form.building" type="text" class="form-control topbar-clearable" placeholder="building" autocomplete="off" @input="debouncedSubmit" @keyup.enter="submit" />
            <span v-if="form.building" class="topbar-clear-btn" @mousedown.prevent="form.building = ''; debouncedSubmit()"><feather-icon icon="XIcon" size="10" /></span>
          </div>
        </div>

        <!-- Bedroom -->
        <div class="topbar-field-sm">
          <label class="topbar-input-label">Bedroom</label>
          <div class="position-relative">
            <input v-model="bedroomInputVal" type="text" class="form-control topbar-clearable" placeholder="0=Studio, 1, 2..." autocomplete="off" @input="onBedroomInput" @keyup.enter="submit" />
            <span v-if="bedroomInputVal" class="topbar-clear-btn" @mousedown.prevent="bedroomInputVal = ''; selectedBedrooms = []; form.bedrooms = ''; debouncedSubmit()"><feather-icon icon="XIcon" size="10" /></span>
          </div>
        </div>

        <!-- Type -->
        <div class="topbar-field-sm">
          <label class="topbar-input-label">Type</label>
          <div class="position-relative">
            <input v-model="form.type_name" type="text" class="form-control topbar-clearable" placeholder="unit type" autocomplete="off" @input="debouncedSubmit" @keyup.enter="submit" />
            <span v-if="form.type_name" class="topbar-clear-btn" @mousedown.prevent="form.type_name = ''; debouncedSubmit()"><feather-icon icon="XIcon" size="10" /></span>
          </div>
        </div>

        <!-- Buttons -->
        <div class="d-flex ml-auto align-items-center" style="gap: 0.25rem; flex-shrink: 0; align-self: center;">
          <b-button variant="flat-secondary" size="sm" class="topbar-icon-btn" @click="collapsed = !collapsed">
            <feather-icon :icon="collapsed ? 'SlidersIcon' : 'ChevronUpIcon'" size="14" />
          </b-button>
          <b-button variant="flat-primary" size="sm" class="topbar-icon-btn" @click="saveTemplate">
            <feather-icon icon="SaveIcon" size="14" />
          </b-button>
        </div>
      </div>
    </div>

    <!-- ── Active filter capsules ── -->
    <div v-if="chips.length && collapsed" class="capsule-row px-1 py-50 border-top">
      <b-badge
        v-for="chip in chips"
        :key="chip.key"
        class="capsule-tag mr-25 mb-25 d-inline-flex align-items-center cursor-pointer"
        @click="clearChip(chip.key)"
      >
        <span class="capsule-label">{{ chip.label }}</span>
        <span class="capsule-value">{{ chip.value }}</span>
        <feather-icon icon="XIcon" size="10" class="ml-25 capsule-x" />
      </b-badge>
      <b-badge class="capsule-tag capsule-clear mr-25 mb-25 d-inline-flex align-items-center cursor-pointer" @click="reset">
        <feather-icon icon="XIcon" size="10" class="mr-25" />Clear All
      </b-badge>
    </div>

    <!-- ── Collapsible advanced-filter body (inline slide-down, both mobile & desktop) ── -->
    <b-collapse :visible="!collapsed">
      <hr class="my-0" />
      <div class="filter-body px-1 pt-1 pb-75">

        <!-- Row 1: Action · Type · Bedroom · Furniture -->
        <b-row>
          <b-col cols="6" sm="4" md="2" class="mb-1">
            <label class="field-label">Action</label>
            <div class="toggle-group border rounded">
              <b-button
                v-for="opt in actionOptions"
                :key="opt.value"
                :variant="isActionActive(opt.value) ? 'primary' : 'flat-secondary'"
                class="toggle-btn"
                @click="toggleAction(opt.value)"
              >{{ opt.text }}</b-button>
            </div>
          </b-col>

          <b-col cols="6" sm="4" md="2" class="mb-1">
            <label class="field-label">Property Type</label>
            <v-select
              v-model="form.property_type"
              :options="propertyTypes"
              label="name"
              :reduce="o => o.id"
              placeholder="All"
            />
          </b-col>

          <b-col cols="12" sm="12" md="5" class="mb-1">
            <label class="field-label">Bedroom</label>
            <div class="toggle-group border rounded">
              <b-button
                v-for="(label, i) in bedroomLabels"
                :key="i"
                :variant="selectedBedrooms.includes(i) ? 'primary' : 'flat-secondary'"
                class="toggle-btn"
                @click="toggleBedroom(i)"
              >{{ label }}</b-button>
            </div>
          </b-col>

          <!-- Building: desktop already has this in the Quick Search topbar; mobile has no equivalent, so add it here -->
          <b-col cols="6" class="mb-1 d-lg-none position-relative">
            <label class="field-label">Building</label>
            <input
              v-model="form.building"
              type="text"
              class="form-control"
              placeholder="building"
              autocomplete="off"
              @focus="onBuildingFocus"
              @input="onBuildingInput"
              @keydown.down.prevent="buildingIndex = Math.min(buildingIndex + 1, filteredBuildings.length - 1)"
              @keydown.up.prevent="buildingIndex = Math.max(buildingIndex - 1, 0)"
              @keydown.enter.prevent="buildingIndex >= 0 ? selectBuilding(buildingIndex) : submit()"
              @blur="hideBuildingDrop"
            />
            <ul
              v-if="showBuildingSugg && filteredBuildings.length"
              class="list-group position-absolute zindex-dropdown shadow-sm"
              style="top: 100%; left: 0; width: 100%; max-height: 200px; overflow-y: auto;"
            >
              <li
                v-for="(item, i) in filteredBuildings"
                :key="'m-bld-' + i"
                class="list-group-item list-group-item-action py-1"
                :class="{ active: i === buildingIndex }"
                @mousedown.prevent="selectBuilding(i)"
              >{{ item.label }}</li>
            </ul>
          </b-col>

          <!-- Unit Type: desktop already has this ("Type") in the Quick Search topbar; mobile has no equivalent, so add it here -->
          <b-col cols="6" class="mb-1 d-lg-none position-relative">
            <label class="field-label">Unit Type</label>
            <input
              v-model="form.type_name"
              type="text"
              class="form-control"
              placeholder="unit type"
              autocomplete="off"
              @focus="onUnitTypeFocus"
              @input="onUnitTypeInput"
              @keydown.down.prevent="unitTypeIndex = Math.min(unitTypeIndex + 1, filteredUnitTypes.length - 1)"
              @keydown.up.prevent="unitTypeIndex = Math.max(unitTypeIndex - 1, 0)"
              @keydown.enter.prevent="unitTypeIndex >= 0 ? selectUnitType(unitTypeIndex) : submit()"
              @blur="hideUnitTypeDrop"
            />
            <ul
              v-if="showUnitTypeSugg && filteredUnitTypes.length"
              class="list-group position-absolute zindex-dropdown shadow-sm"
              style="top: 100%; left: 0; width: 100%; max-height: 200px; overflow-y: auto;"
            >
              <li
                v-for="(item, i) in filteredUnitTypes"
                :key="'m-ut-' + i"
                class="list-group-item list-group-item-action py-1"
                :class="{ active: i === unitTypeIndex }"
                @mousedown.prevent="selectUnitType(i)"
              >{{ item.label }}</li>
            </ul>
          </b-col>

          <b-col cols="12" sm="4" md="3" class="mb-1">
            <label class="field-label">Furniture</label>
            <v-select
              v-model="furnitureSelected"
              multiple
              label="name"
              :reduce="o => o.id"
              :options="furnitureTypes"
              placeholder="Any"
              @input="onFurnitureChange"
            />
          </b-col>
        </b-row>

        <!-- Row 2: Price · SQM · Zone -->
        <b-row>
          <b-col cols="12" sm="6" md="4" class="mb-1">
            <label class="field-label">Price (฿)</label>
            <div ref="priceRef" class="position-relative">
              <b-input-group class="range-group">
                <input type="text" :value="fmt(form.priceMin)" placeholder="Min" autocomplete="off" class="form-control text-center" @focus="showPriceDrop = true" @input="onFmtInput($event, 'priceMin')" />
                <b-input-group-text class="range-sep">–</b-input-group-text>
                <input type="text" :value="fmt(form.priceMax)" placeholder="Max" autocomplete="off" class="form-control text-center" @focus="showPriceDrop = true" @input="onFmtInput($event, 'priceMax')" />
              </b-input-group>
              <div v-if="showPriceDrop" class="range-dropdown border rounded bg-white shadow-sm">
                <b-list-group flush>
                  <b-list-group-item v-for="(r, i) in priceRanges" :key="i" button class="d-flex align-items-center py-1" @click="selectPriceRange(r)">
                    <b-form-radio :value="r.value" v-model="selectedPriceVal" class="mr-2" name="priceRad" />{{ r.text }}
                  </b-list-group-item>
                </b-list-group>
              </div>
            </div>
          </b-col>

          <b-col cols="12" sm="6" md="4" class="mb-1">
            <label class="field-label">SQM</label>
            <div ref="sqmRef" class="position-relative">
              <b-input-group class="range-group">
                <input type="text" :value="fmt(form.sqm_min)" placeholder="Min" autocomplete="off" class="form-control text-center" @focus="showSqmDrop = true" @input="onFmtInput($event, 'sqm_min')" />
                <b-input-group-text class="range-sep">–</b-input-group-text>
                <input type="text" :value="fmt(form.sqm_max)" placeholder="Max" autocomplete="off" class="form-control text-center" @focus="showSqmDrop = true" @input="onFmtInput($event, 'sqm_max')" />
              </b-input-group>
              <div v-if="showSqmDrop" class="range-dropdown border rounded bg-white shadow-sm">
                <b-list-group flush>
                  <b-list-group-item v-for="(r, i) in sqmRanges" :key="i" button class="d-flex align-items-center py-1" @click="selectSqmRange(r)">
                    <b-form-radio :value="r.value" v-model="selectedSqmVal" class="mr-2" name="sqmRad" />{{ r.text }}
                  </b-list-group-item>
                </b-list-group>
              </div>
            </div>
          </b-col>

          <b-col cols="12" sm="12" md="4" class="mb-1">
            <label class="field-label">Zone</label>
            <v-select
              v-model="zonesSelected"
              multiple
              label="title"
              :reduce="o => o.id"
              :options="zones.map(z => ({ title: z.name[$i18n.locale], id: z.id }))"
              placeholder="All zones"
              @input="onZoneChange"
            />
          </b-col>
        </b-row>

        <!-- Row 3: Transit · Expire · Available -->
        <b-row>
          <b-col cols="12" md="6" class="mb-1">
            <label class="field-label">Transit</label>
            <b-tabs small class="filter-tabs">
              <b-tab title="BTS">
                <v-select v-model="btsSelected" multiple label="name" :reduce="o => o.id" :options="btsList" placeholder="BTS stations..." @input="onTransitChange('bts')" />
              </b-tab>
              <b-tab title="MRT">
                <v-select v-model="mrtSelected" multiple label="name" :reduce="o => o.id" :options="mrtList" placeholder="MRT stations..." @input="onTransitChange('mrt')" />
              </b-tab>
              <b-tab title="ARL">
                <v-select v-model="arlSelected" multiple label="name" :reduce="o => o.id" :options="arlList.map(a => ({ name: a.name[$i18n.locale], id: a.id }))" placeholder="ARL stations..." @input="onTransitChange('arl')" />
              </b-tab>
            </b-tabs>
          </b-col>

          <b-col cols="12" sm="6" md="3" class="mb-1">
            <label class="field-label">Expire Date</label>
            <date-range-picker ref="expirePicker" opens="right" :locale-data="localeData" :ranges="false" :autoApply="true" v-model="expireRange" @update="onExpireRangeUpdate" class="w-100">
              <template v-slot:input="p"><div class="text-center w-100">{{ p.startDate | fmtDate }} – {{ p.endDate | fmtDate }}</div></template>
              <template v-slot:footer>
                <div class="text-center p-2 border-top">
                  <b-button size="sm" variant="outline-danger" @click="clearExpire">Clear</b-button>
                </div>
              </template>
            </date-range-picker>
          </b-col>

          <b-col cols="12" sm="6" md="3" class="mb-1">
            <label class="field-label">Available Date</label>
            <date-range-picker ref="avlPicker" opens="left" :locale-data="localeData" :ranges="false" :autoApply="true" v-model="avlRange" @update="onAvlRangeUpdate" class="w-100">
              <template v-slot:input="p"><div class="text-center w-100">{{ p.startDate | fmtDate }} – {{ p.endDate | fmtDate }}</div></template>
              <template v-slot:footer>
                <div class="text-center p-2 border-top">
                  <b-button size="sm" variant="outline-danger" @click="clearAvl">Clear</b-button>
                </div>
              </template>
            </date-range-picker>
          </b-col>
        </b-row>

        <!-- Facility & Amenity (collapsible) -->
        <div class="mt-25">
          <b-button variant="link" size="sm" class="p-0 text-muted d-flex align-items-center mb-75" style="text-decoration: none;" @click="showFac = !showFac">
            <feather-icon :icon="showFac ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="13" class="mr-25" />
            Facility &amp; Amenity
          </b-button>
          <b-collapse :visible="showFac">
            <b-tabs class="filter-tabs">
              <b-tab title="Facility">
                <b-form-checkbox-group v-model="facSelected" :options="facList.map(f => ({ text: f.name[$i18n.locale], value: f.id }))" class="checkbox-grid mb-1" @change="onFacChange" />
              </b-tab>
              <b-tab title="Amenity">
                <b-form-checkbox-group v-model="amenitySelected" :options="amenityList.map(a => ({ text: a.name[$i18n.locale], value: a.id }))" class="checkbox-grid mb-1" @change="onAmenityChange" />
              </b-tab>
            </b-tabs>
          </b-collapse>
        </div>

        <!-- Footer -->
        <div class="d-flex justify-content-end align-items-center pt-75 border-top mt-75" style="gap: 0.5rem;">
          <b-button variant="outline-secondary" size="sm" @click="reset">
            <feather-icon icon="XIcon" size="13" class="mr-25" />Clear All
          </b-button>
          <b-button variant="primary" size="sm" @click="submit">
            <feather-icon icon="SearchIcon" size="13" class="mr-25" />Search
          </b-button>
        </div>

      </div>
    </b-collapse>
  </b-card>
</template>

<script>
import axios from '@axios'
import { MIN_PLACE_KEYWORD, searchPlaces } from '@core/utils/place-autocomplete'
import moment from 'moment'
import {
  BCard, BCollapse, BButton, BRow, BCol, BFormCheckbox, BFormCheckboxGroup,
  BFormInput, BFormRadio, BInputGroup, BInputGroupPrepend, BInputGroupAppend, BInputGroupText,
  BTabs, BTab, BListGroup, BListGroupItem, BDropdown, BDropdownItem, BBadge,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const PRICE_SALE = [
  { min: null, max: null,     text: 'All Price',    value: '' },
  { min: null, max: 15000000, text: '< 15 M',       value: '0-15' },
  { min: 15000000, max: 20000000, text: '15 – 20 M', value: '15-20' },
  { min: 20000000, max: 25000000, text: '20 – 25 M', value: '20-25' },
  { min: 25000000, max: 30000000, text: '25 – 30 M', value: '25-30' },
  { min: 30000000, max: 35000000, text: '30 – 35 M', value: '30-35' },
  { min: 35000000, max: null,     text: '> 35 M',    value: '35-0' },
]
const PRICE_RENT = [
  { min: null, max: null,   text: 'All Price',        value: '' },
  { min: null, max: 30000,  text: '< 30,000',         value: '0-30000' },
  { min: 30000, max: 50000, text: '30,000 – 50,000',  value: '30000-50000' },
  { min: 50000, max: 80000, text: '50,000 – 80,000',  value: '50000-80000' },
  { min: 80000, max: null,  text: '> 80,000',         value: '80000-0' },
]
const SQM_RANGES = [
  { min: null, max: null,  text: 'All Size',        value: '' },
  { min: 60,  max: 80,     text: '60 – 80 sqm.',    value: '60-80' },
  { min: 80,  max: 130,    text: '80 – 130 sqm.',   value: '80-130' },
  { min: 130, max: 160,    text: '130 – 160 sqm.',  value: '130-160' },
  { min: 160, max: 250,    text: '160 – 250 sqm.',  value: '160-250' },
  { min: 250, max: null,   text: '250+ sqm.',        value: '250-0' },
]

export default {
  name: 'FilterPanelV2',
  components: {
    BCard, BCollapse, BButton, BRow, BCol, BFormCheckbox, BFormCheckboxGroup,
    BFormInput, BFormRadio, BInputGroup, BInputGroupPrepend, BInputGroupAppend, BInputGroupText,
    BTabs, BTab, BListGroup, BListGroupItem, BDropdown, BDropdownItem, BBadge,
    vSelect, DateRangePicker,
  },
  filters: {
    fmtDate(v) { return v ? moment(v).format('DD/MM/YYYY') : '–' },
  },
  props: {
    initialFilter:       { type: Object,  default: () => ({}) },
    toolbarStatus:       { type: Object,  default: null },
    toolbarExpiringSoon: { type: Boolean, default: false },
  },
  data() {
    return {
      collapsed: true,
      keywordSheetOpen: false,
      lockedScrollY: 0,
      showFac: false,
      showPriceDrop: false,
      showSqmDrop: false,

      form: {
        keyword: '',
        propertyfilter: '',
        action_id: '',
        property_type: null,
        bedrooms: '',
        priceMin: null,
        priceMax: null,
        sqm_min: null,
        sqm_max: null,
        bts_ids: '',
        mrt_ids: '',
        arl_ids: '',
        zones: '',
        minDateExpire: null,
        maxDateExpire: null,
        minDateAvl: null,
        maxDateAvl: null,
        place_id: null,
        owner_id: null,
        unit: '',
        floor: '',
        type_name: '',
        building: '',
        fac_ids: '',
        amenity_ids: '',
        furniture_ids: '',
        rentPriceMin: null,
        rentPriceMax: null,
        salePriceMin: null,
        salePriceMax: null,
      },

      // UI-only selections
      actionOptions: [
        { value: '2,4', text: 'Rent' },
        { value: '2,3', text: 'Sale' },
      ],
      actionSelected: [],
      selectedBedrooms: [],
      zonesSelected: [],
      btsSelected: [],
      mrtSelected: [],
      arlSelected: [],
      facSelected: [],
      amenitySelected: [],
      furnitureSelected: [],
      selectedPriceVal: '',
      selectedSqmVal: '',

      // Snapshot of applied filter — chips are derived from this, not from live form
      appliedForm: null,
      activeTemplate: null,
      appliedPlaceSearch: '',
      appliedOwnerSearch: '',
      appliedActionSelected: [],
      appliedPropertyTypeName: '',

      // Autocomplete
      placeSearch: '',
      filteredPlaces: [],
      showPlaceSugg: false,
      placeIndex: -1,
      ownerSearch: '',
      filteredOwners: [],
      showOwnerSugg: false,
      ownerIndex: -1,
      filteredBuildings: [],
      showBuildingSugg: false,
      buildingIndex: -1,
      filteredUnitTypes: [],
      showUnitTypeSugg: false,
      unitTypeIndex: -1,

      // Keyword autocomplete
      kwSuggestions: [],
      showKwSuggestions: false,
      kwIndex: -1,
      kwDebounce: null,
      submitDebounceTimer: null,
      kwWasPasted: false,
      kwScrollBlur: false,
      kwIsPasting: false,
      kwAutoResolvedType: null,
      placeDebounce: null,
      placeRequestSeq: 0,
      ownerDebounce: null,
      ownerRequestSeq: 0,
      buildingDebounce: null,
      buildingRequestSeq: 0,
      unitTypeDebounce: null,
      unitTypeRequestSeq: 0,

      // Date ranges
      expireRange: { startDate: null, endDate: null },
      avlRange: { startDate: null, endDate: null },
      localeData: { firstDay: 1, format: 'DD-MM-YYYY' },

      // Reference data
      propertyTypes: [],
      zones: [],
      btsList: [],
      mrtList: [],
      arlList: [],
      facList: [],
      amenityList: [],
      furnitureTypes: [],
      templateData: [],

      bedroomInputVal: '',
      bedroomLabels: ['Studio', '1', '2', '3', '4+'],
      sqmRanges: SQM_RANGES,

      userData: JSON.parse(localStorage.getItem('userData') || '{}'),
    }
  },
  watch: {
    selectedBedrooms(val) {
      this.bedroomInputVal = val.map(i => i === 0 ? '0' : String(i)).join(',')
    },
    // Lock background scroll while the mobile full-screen keyword sheet is open.
    // `overflow: hidden` alone doesn't stop iOS Safari from scrolling the page
    // when a fixed-position input is focused. Pin BOTH <html> and <body> with
    // position:fixed (not just overflow:hidden) so there is no scrollable
    // ancestor left in the DOM at all, plus a window 'scroll' listener that
    // snaps straight back to 0 as a safety net for versions that still try.
    keywordSheetOpen(val) {
      if (val) {
        this.lockedScrollY = window.scrollY || window.pageYOffset || 0
        document.documentElement.style.position = 'fixed'
        document.documentElement.style.top = '0'
        document.documentElement.style.left = '0'
        document.documentElement.style.right = '0'
        document.documentElement.style.width = '100%'
        document.documentElement.style.height = '100%'
        document.documentElement.style.overflow = 'hidden'
        document.body.style.position = 'fixed'
        document.body.style.top = `-${this.lockedScrollY}px`
        document.body.style.left = '0'
        document.body.style.right = '0'
        document.body.style.width = '100%'
        document.body.style.height = '100%'
        document.body.style.overflow = 'hidden'
        window.addEventListener('scroll', this.pinBackgroundScroll)
      } else {
        window.removeEventListener('scroll', this.pinBackgroundScroll)
        document.documentElement.style.position = ''
        document.documentElement.style.top = ''
        document.documentElement.style.left = ''
        document.documentElement.style.right = ''
        document.documentElement.style.width = ''
        document.documentElement.style.height = ''
        document.documentElement.style.overflow = ''
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.left = ''
        document.body.style.right = ''
        document.body.style.width = ''
        document.body.style.height = ''
        document.body.style.overflow = ''
        window.scrollTo(0, this.lockedScrollY)
      }
    },
  },
  computed: {
    chips() {
      const f = this.appliedForm
      if (!f && !this.toolbarStatus && !this.toolbarExpiringSoon) return []
      const list = []
      if (this.toolbarStatus)       list.push({ key: '_toolbar_status',   label: 'Status',        value: this.toolbarStatus.name })
      if (this.toolbarExpiringSoon) list.push({ key: '_toolbar_expiring', label: 'Expiring Soon', value: '2 months' })
      if (!f) return list

      if (f.action_id) {
        const ids = f.action_id.toString().split(',').map(Number).filter(Boolean)
        const bothSelected = this.appliedActionSelected.length === 2
        const label = bothSelected || (ids.includes(4) && ids.includes(3))
          ? 'Sale & Rent'
          : ids.includes(4) ? 'Rent'
          : ids.includes(3) ? 'Sale'
          : 'Sale & Rent'
        list.push({ key: 'action_id', label: 'Action', value: label })
      }
      if (f.property_type) {
        list.push({ key: 'property_type', label: 'Property Type', value: this.appliedPropertyTypeName || f.property_type })
      }
      if (f.bedrooms) {
        const beds = f.bedrooms.split(',').map(n => n === '0' ? 'Studio' : n).join(', ')
        list.push({ key: 'bedrooms', label: 'Bedroom', value: beds })
      }
      if (f.priceMin || f.priceMax) {
        const min = f.priceMin ? '฿' + Number(f.priceMin).toLocaleString() : '฿0'
        const max = f.priceMax ? '฿' + Number(f.priceMax).toLocaleString() : '∞'
        list.push({ key: 'price', label: 'Price', value: `${min} – ${max}` })
      }
      if (f.sqm_min || f.sqm_max) {
        list.push({ key: 'sqm', label: 'SQM', value: `${f.sqm_min || 0} – ${f.sqm_max || '∞'}` })
      }
      if (f.keyword)        list.push({ key: 'keyword',        label: 'Search',     value: f.keyword })
      if (f.propertyfilter) list.push({ key: 'propertyfilter', label: 'Code / Name', value: f.propertyfilter })
      if (this.appliedPlaceSearch) list.push({ key: 'place_id', label: 'Property', value: this.appliedPlaceSearch })
      if (this.appliedOwnerSearch) list.push({ key: 'owner_id', label: 'Owner',    value: this.appliedOwnerSearch })
      if (f.zones) {
        const n = f.zones.split(',').filter(Boolean).length
        list.push({ key: 'zones', label: 'Zone', value: `${n} selected` })
      }
      if (f.bts_ids) {
        const n = f.bts_ids.split(',').filter(Boolean).length
        list.push({ key: 'bts_ids', label: 'BTS', value: `${n} selected` })
      }
      if (f.mrt_ids) {
        const n = f.mrt_ids.split(',').filter(Boolean).length
        list.push({ key: 'mrt_ids', label: 'MRT', value: `${n} selected` })
      }
      if (f.arl_ids) {
        const n = f.arl_ids.split(',').filter(Boolean).length
        list.push({ key: 'arl_ids', label: 'ARL', value: `${n} selected` })
      }
      if (f.unit)       list.push({ key: 'unit',      label: 'Unit',      value: f.unit })
      if (f.floor)      list.push({ key: 'floor',     label: 'Floor',     value: f.floor })
      if (f.building)   list.push({ key: 'building',  label: 'Building',  value: f.building })
      if (f.type_name)  list.push({ key: 'type_name', label: 'Unit Type', value: f.type_name })
      if (f.fac_ids) {
        const n = f.fac_ids.split(',').filter(Boolean).length
        list.push({ key: 'fac_ids', label: 'Facility', value: `${n} selected` })
      }
      if (f.amenity_ids) {
        const n = f.amenity_ids.split(',').filter(Boolean).length
        list.push({ key: 'amenity_ids', label: 'Amenity', value: `${n} selected` })
      }
      if (f.furniture_ids) {
        const names = f.furniture_ids.split(',').map(Number).filter(Boolean)
          .map(id => { const t = this.furnitureTypes.find(o => o.id === id); return t ? t.name : id }).join(', ')
        list.push({ key: 'furniture_ids', label: 'Furniture', value: names || f.furniture_ids })
      }
      if (f.rentPriceMin || f.rentPriceMax) {
        const min = f.rentPriceMin ? '฿' + Number(f.rentPriceMin).toLocaleString() : '฿0'
        const max = f.rentPriceMax ? '฿' + Number(f.rentPriceMax).toLocaleString() : '∞'
        list.push({ key: 'rentPrice', label: 'Rent ฿', value: `${min} – ${max}` })
      }
      if (f.salePriceMin || f.salePriceMax) {
        const min = f.salePriceMin ? '฿' + Number(f.salePriceMin).toLocaleString() : '฿0'
        const max = f.salePriceMax ? '฿' + Number(f.salePriceMax).toLocaleString() : '∞'
        list.push({ key: 'salePrice', label: 'Sale ฿', value: `${min} – ${max}` })
      }
      if (f.minDateExpire) list.push({ key: 'expire', label: 'Expire', value: `${f.minDateExpire} – ${f.maxDateExpire}` })
      if (f.minDateAvl)    list.push({ key: 'avl',    label: 'Avl Date', value: `${f.minDateAvl} – ${f.maxDateAvl}` })

      return list
    },
    priceRanges() {
      const action = String(this.form.action_id || '')
      return action.includes('4') ? PRICE_RENT : PRICE_SALE
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.onClickOutside)
    Promise.all([
      this.loadTypes(), this.loadZones(),
      this.loadBts(), this.loadMrt(), this.loadArl(),
      this.loadFacilities(), this.loadAmenities(),
      this.loadFurniture(), this.loadTemplates(),
    ])
    this.applyInitialFilter()
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onClickOutside)
    clearTimeout(this.kwDebounce)
    clearTimeout(this.placeDebounce)
    clearTimeout(this.ownerDebounce)
    clearTimeout(this.buildingDebounce)
    clearTimeout(this.unitTypeDebounce)
    clearTimeout(this.submitDebounceTimer)
    window.removeEventListener('scroll', this.pinBackgroundScroll)
    document.documentElement.style.position = ''
    document.documentElement.style.top = ''
    document.documentElement.style.left = ''
    document.documentElement.style.right = ''
    document.documentElement.style.width = ''
    document.documentElement.style.height = ''
    document.documentElement.style.overflow = ''
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.left = ''
    document.body.style.right = ''
    document.body.style.width = ''
    document.body.style.height = ''
    document.body.style.overflow = ''
  },
  methods: {
    // ── Mobile full-screen keyword sheet ──
    pinBackgroundScroll() {
      if (this.keywordSheetOpen && (window.scrollX || window.scrollY)) window.scrollTo(0, 0)
    },
    blurKeywordInput() {
      const el = this.$refs.sheetKeywordInput
      if (el && document.activeElement === el) {
        this.kwScrollBlur = true

        el.setAttribute('readonly', 'readonly')
        el.blur()
        setTimeout(() => { if (this.$refs.sheetKeywordInput) this.$refs.sheetKeywordInput.removeAttribute('readonly') }, 100)
      }
    },
    openKeywordSheet() {
      this.keywordSheetOpen = true
      this.$nextTick(() => {
        if (this.$refs.sheetKeywordInput) this.$refs.sheetKeywordInput.focus()
      })
    },
    closeKeywordSheet() {
      this.keywordSheetOpen = false
      this.showKwSuggestions = false
    },
    submitKeyword() {
      this.submit()
      this.closeKeywordSheet()
    },

    // ── Init ──
    applyInitialFilter() {
      const f = this.initialFilter
      if (!f || !Object.keys(f).length) return
      Object.keys(this.form).forEach(k => { if (f[k] !== undefined) this.form[k] = f[k] })
      if (f.action_id) this.actionSelected = f.action_id.toString().split(',').filter(Boolean)
      if (f.bedrooms)  this.selectedBedrooms = f.bedrooms.toString().split(',').map(Number)
      if (f.zones)     this.zonesSelected    = f.zones.toString().split(',').map(Number)
      if (f.bts_ids)   this.btsSelected      = f.bts_ids.toString().split(',').map(Number)
      if (f.mrt_ids)   this.mrtSelected      = f.mrt_ids.toString().split(',').map(Number)
      if (f.arl_ids)   this.arlSelected      = f.arl_ids.toString().split(',').map(Number)
      if (f.fac_ids)   this.facSelected      = f.fac_ids.toString().split(',').map(Number)
      if (f.amenity_ids) this.amenitySelected = f.amenity_ids.toString().split(',').map(Number)
      if (f.furniture_ids) this.furnitureSelected = f.furniture_ids.toString().split(',').map(Number)
      if (f.minDateExpire && f.maxDateExpire) {
        this.expireRange = { startDate: moment(f.minDateExpire), endDate: moment(f.maxDateExpire) }
      }
      if (f.minDateAvl && f.maxDateAvl) {
        this.avlRange = { startDate: moment(f.minDateAvl), endDate: moment(f.maxDateAvl) }
      }
      if (f.place_name) { this.placeSearch = f.place_name; this.appliedPlaceSearch = f.place_name }
      if (f.owner_name) { this.ownerSearch = f.owner_name; this.appliedOwnerSearch = f.owner_name }
      this.appliedForm = { ...this.form }
    },

    // ── Top bar bedroom input ──
    onBedroomInput() {
      const val = this.bedroomInputVal.trim()
      if (!val) { this.selectedBedrooms = []; this.form.bedrooms = ''; }
      else {
        const nums = val.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n >= 0 && n <= 4)
        this.selectedBedrooms = [...new Set(nums)]
        this.form.bedrooms = this.selectedBedrooms.join(',')
      }
      this.debouncedSubmit()
    },

    // ── Action toggle ──
    isActionActive(val) { return this.actionSelected.includes(val) },
    toggleAction(val) {
      const i = this.actionSelected.indexOf(val)
      if (i > -1) this.actionSelected.splice(i, 1)
      else this.actionSelected.push(val)

      if (!this.actionSelected.length) { this.form.action_id = ''; return }
      const all = this.actionSelected.flatMap(v => v.split(',').map(Number))
      const dups = [...new Set(all.filter((v, i, s) => s.indexOf(v) !== i))]
      this.form.action_id = dups.length ? dups.join(',') : [...new Set(all)].join(',')
      this.form.priceMin = null
      this.form.priceMax = null
      this.selectedPriceVal = ''
    },

    // ── Bedroom toggle ──
    toggleBedroom(i) {
      const idx = this.selectedBedrooms.indexOf(i)
      if (idx > -1) this.selectedBedrooms.splice(idx, 1)
      else this.selectedBedrooms.push(i)
      this.form.bedrooms = this.selectedBedrooms.join(',')
    },

    // ── Price / SQM ranges ──
    fmt(val) {
      if (val === null || val === undefined || val === '') return ''
      const n = Number(String(val).replace(/,/g, ''))
      return isNaN(n) ? '' : n.toLocaleString('en-US')
    },
    onFmtInput(e, field) {
      const digits = e.target.value.replace(/\D/g, '')
      this.form[field] = digits ? parseInt(digits, 10) : null
    },
    selectPriceRange(r) {
      this.form.priceMin = r.min ?? null
      this.form.priceMax = r.max ?? null
      this.selectedPriceVal = r.value
      this.showPriceDrop = false
    },
    selectSqmRange(r) {
      this.form.sqm_min = r.min ?? null
      this.form.sqm_max = r.max ?? null
      this.selectedSqmVal = r.value
      this.showSqmDrop = false
    },
    onClickOutside(e) {
      if (this.$refs.priceRef && !this.$refs.priceRef.contains(e.target)) this.showPriceDrop = false
      if (this.$refs.sqmRef   && !this.$refs.sqmRef.contains(e.target))   this.showSqmDrop = false
    },

    // ── Status ──
    // ── Zone ──
    onZoneChange() {
      this.form.zones = this.zonesSelected.join(',')
    },

    // ── Transit ──
    onTransitChange(type) {
      if (type === 'bts') this.form.bts_ids = this.btsSelected.join(',')
      if (type === 'mrt') this.form.mrt_ids = this.mrtSelected.join(',')
      if (type === 'arl') this.form.arl_ids = this.arlSelected.join(',')
    },

    // ── Facility / Amenity ──
    onFacChange()     { this.form.fac_ids     = this.facSelected.join(',') },
    onAmenityChange() { this.form.amenity_ids = this.amenitySelected.join(',') },
    onFurnitureChange() { this.form.furniture_ids = this.furnitureSelected.join(',') },

    // ── Property name autocomplete ──
    onPlaceInput(val) {
      this.placeIndex = -1
      clearTimeout(this.placeDebounce)
      if (!val || val.trim().length < MIN_PLACE_KEYWORD) { this.filteredPlaces = []; this.showPlaceSugg = false; return }
      const seq = ++this.placeRequestSeq
      this.placeDebounce = setTimeout(async () => {
        try {
          // Unpaged this cost 6.3 MB and 17 s on 2 characters; see place-autocomplete.js
          const places = await searchPlaces(val)
          if (seq !== this.placeRequestSeq) return
          this.filteredPlaces = places
          this.showPlaceSugg = this.filteredPlaces.length > 0
        } catch { if (seq === this.placeRequestSeq) this.filteredPlaces = [] }
      }, 300)
    },
    selectPlace(i) {
      const item = this.filteredPlaces[i]
      if (item) {
        const locale = this.$i18n.locale
        this.placeSearch = item.name && item.name[locale] ? item.name[locale] : item.name
        this.form.place_id = item.id
        this.filteredPlaces = []
        this.showPlaceSugg = false
      }
    },

    // ── Owner autocomplete ──
    onOwnerInput() {
      const val = this.ownerSearch
      this.ownerIndex = -1
      this.form.owner_id = null
      clearTimeout(this.ownerDebounce)
      this.debouncedSubmit()
      if (!val || val.trim().length < 2) { this.filteredOwners = []; this.showOwnerSugg = false; return }
      const seq = ++this.ownerRequestSeq
      this.ownerDebounce = setTimeout(async () => {
        try {
          const res = await axios.get(`/property/v1/owner/${val.trim()}`)
          if (seq !== this.ownerRequestSeq) return
          this.filteredOwners = res.data.data || []
          this.showOwnerSugg = this.filteredOwners.length > 0
        } catch { if (seq === this.ownerRequestSeq) this.filteredOwners = [] }
      }, 300)
    },
    selectOwner(i) {
      const item = this.filteredOwners[i]
      if (item) {
        this.ownerSearch = `${item.firstname} ${item.lastname}`
        this.form.owner_id = item.id
        this.filteredOwners = []
        this.showOwnerSugg = false
        this.$nextTick(() => this.submit())
      }
    },
    hideOwnerDrop() { setTimeout(() => { this.showOwnerSugg = false }, 160) },
    hidePropFilterSugg() { setTimeout(() => { this.showPlaceSugg = false }, 160) },

    // ── Building autocomplete ──
    onBuildingFocus() {
      if (!this.filteredBuildings.length) this.fetchBuildingSuggestions()
    },
    onBuildingInput() {
      this.buildingIndex = -1
      clearTimeout(this.buildingDebounce)
      this.buildingDebounce = setTimeout(() => this.fetchBuildingSuggestions(), 300)
    },
    async fetchBuildingSuggestions() {
      const seq = ++this.buildingRequestSeq
      try {
        const res = await axios.post('property/v1/buildings', {
          keyword: this.form.building ? this.form.building.trim() : undefined,
          bts_ids: this.form.bts_ids || null,
          mrt_ids: this.form.mrt_ids || null,
          arl_ids: this.form.arl_ids || null,
          zone_ids: this.form.zones || null,
        })
        if (seq !== this.buildingRequestSeq) return
        this.filteredBuildings = res.data.data || []
        this.showBuildingSugg = this.filteredBuildings.length > 0
      } catch { if (seq === this.buildingRequestSeq) this.filteredBuildings = [] }
    },
    selectBuilding(i) {
      const item = this.filteredBuildings[i]
      if (item) {
        this.form.building = item.value
        this.filteredBuildings = []
        this.showBuildingSugg = false
      }
    },
    hideBuildingDrop() { setTimeout(() => { this.showBuildingSugg = false }, 160) },

    // ── Unit Type autocomplete ──
    onUnitTypeFocus() {
      if (!this.filteredUnitTypes.length) this.fetchUnitTypeSuggestions()
    },
    onUnitTypeInput() {
      this.unitTypeIndex = -1
      clearTimeout(this.unitTypeDebounce)
      this.unitTypeDebounce = setTimeout(() => this.fetchUnitTypeSuggestions(), 300)
    },
    async fetchUnitTypeSuggestions() {
      const seq = ++this.unitTypeRequestSeq
      try {
        const res = await axios.post('property/v1/unit-types', {
          keyword: this.form.type_name ? this.form.type_name.trim() : undefined,
          bts_ids: this.form.bts_ids || null,
          mrt_ids: this.form.mrt_ids || null,
          arl_ids: this.form.arl_ids || null,
          zone_ids: this.form.zones || null,
        })
        if (seq !== this.unitTypeRequestSeq) return
        this.filteredUnitTypes = res.data.data || []
        this.showUnitTypeSugg = this.filteredUnitTypes.length > 0
      } catch { if (seq === this.unitTypeRequestSeq) this.filteredUnitTypes = [] }
    },
    selectUnitType(i) {
      const item = this.filteredUnitTypes[i]
      if (item) {
        this.form.type_name = item.value
        this.filteredUnitTypes = []
        this.showUnitTypeSugg = false
      }
    },
    hideUnitTypeDrop() { setTimeout(() => { this.showUnitTypeSugg = false }, 160) },

    // ── Code / Name autocomplete (codes + places) ──
    onPropFilterInput() {
      const val = this.form.propertyfilter
      this.debouncedSubmit()
      this.placeIndex = -1
      clearTimeout(this.placeDebounce)
      if (!val || val.trim().length < 2) { this.filteredPlaces = []; this.showPlaceSugg = false; return }
      const seq = ++this.placeRequestSeq
      this.placeDebounce = setTimeout(async () => {
        try {
          const res = await axios.post('/property/v1/code-name-suggest', { keyword: val.trim() })
          if (seq !== this.placeRequestSeq) return
          this.filteredPlaces = res.data.data || []
          this.showPlaceSugg = this.filteredPlaces.length > 0
        } catch { if (seq === this.placeRequestSeq) this.filteredPlaces = [] }
      }, 300)
    },
    selectPlaceFromFilter(i) {
      const item = this.filteredPlaces[i]
      if (!item) return
      if (item.type === 'place') {
        const locale = this.$i18n.locale
        this.form.propertyfilter = (item.name && item.name[locale]) || item.label || ''
        this.form.place_id = item.id || null
      } else {
        this.form.place_id = null
        this.form.propertyfilter = item.value
      }
      this.filteredPlaces = []
      this.showPlaceSugg = false
      this.placeIndex = -1
      this.$nextTick(() => this.submit())
    },

    // ── Keyword search autocomplete ──
    onKeywordPaste() { this.kwIsPasting = true; this.kwWasPasted = true },
    onKeywordInput() {
      this.kwAutoResolvedType = null
      if (this.kwIsPasting) { this.kwIsPasting = false; this.kwSuggestions = []; this.showKwSuggestions = false; return }
      const val = this.form.keyword
      clearTimeout(this.kwDebounce)
      this.kwIndex = -1
      if (!val || !val.trim()) { this.kwSuggestions = []; this.showKwSuggestions = false; return }
      const trimmed = val.trim()

      // Category word ("building"/"tower"/"type" + optional value) → dedicated
      // distinct-value lookup, so e.g. "building A" finds single-letter building
      // names that a plain substring search across the whole string never could.
      const categoryMatch = trimmed.match(/^(building|tower|type)\s*(.*)$/i)
      if (categoryMatch) {
        const category = categoryMatch[1].toLowerCase()
        const rest = categoryMatch[2].trim()
        const endpoint = category === 'type' ? 'property/v1/unit-types' : 'property/v1/buildings'
        const itemType = category === 'type' ? 'unit_type' : 'building'
        this.kwDebounce = setTimeout(async () => {
          try {
            const res = await axios.post(endpoint, {
              keyword: rest || undefined,
              bts_ids: this.form.bts_ids || null,
              mrt_ids: this.form.mrt_ids || null,
              arl_ids: this.form.arl_ids || null,
              zone_ids: this.form.zones || null,
            })
            this.kwSuggestions = (res.data.data || []).map(item => ({ type: itemType, label: item.label, value: item.value }))
            this.showKwSuggestions = this.kwSuggestions.length > 0
          } catch { this.kwSuggestions = []; this.showKwSuggestions = false }
        }, 300)
        return
      }

      const isFloor = /^\d{1,2}$/.test(trimmed)
      const isCode  = /^[Pp]/.test(trimmed)

      // Single digit → floor suggestion only, no API needed
      if (trimmed.length === 1 && isFloor) {
        this.kwSuggestions = [{ type: 'floor', label: `Floor ${trimmed}`, value: trimmed }]
        this.showKwSuggestions = true
        return
      }

      if (trimmed.length < 2) { this.kwSuggestions = []; this.showKwSuggestions = false; return }
      this.kwDebounce = setTimeout(async () => {
        try {
          const res = await axios.post('v2/search/property', { keyword: trimmed, bts_ids: this.form.bts_ids || null, mrt_ids: this.form.mrt_ids || null, arl_ids: this.form.arl_ids || null, zone_ids: this.form.zones || null })
          const results = res.data.data || []

          if (isFloor) results.unshift({ type: 'floor',    label: `Floor ${trimmed}`, value: trimmed })
          if (isCode)  results.unshift({ type: 'code',     label: trimmed.toUpperCase(), value: trimmed.toUpperCase() })

          this.kwSuggestions = results
          this.showKwSuggestions = this.kwSuggestions.length > 0
        } catch { this.kwSuggestions = []; this.showKwSuggestions = false }
      }, 300)
    },
    onKeywordEnter() {
      if (this.kwIndex >= 0) { this.selectKwSuggestion(this.kwIndex); return }
      this.showKwSuggestions = false
      this.submitKeyword()
    },
    selectKwSuggestion(i) {
      const item = this.kwSuggestions[i]
      if (!item) return
      // Keep the label in the search input so user sees what they selected
      this.form.keyword = item.label
      this.form.propertyfilter = ''
      this.kwAutoResolvedType = null

      if (item.type === 'code') {
        this.form.propertyfilter = item.value
        // keyword stays — shows "Search" pill with the code label
      } else if (item.type === 'building') {
        this.form.building = item.value
        this.kwAutoResolvedType = 'building'
      } else if (item.type === 'unit_type') {
        this.form.type_name = item.value
        this.kwAutoResolvedType = 'unit_type'
      } else if (item.type === 'floor') {
        this.form.floor = item.value
        this.kwAutoResolvedType = 'floor'
      } else if (item.type === 'unit_no') {
        this.form.unit = item.value
        this.kwAutoResolvedType = 'unit_no'
      } else if (item.type === 'house_no') {
        this.form.unit = item.value
        this.kwAutoResolvedType = 'house_no'
      } else if (item.place_id) {
        this.form.place_id = item.place_id
        this.placeSearch = item.label
        this.kwAutoResolvedType = 'place'
      } else if (item.owner_id) {
        this.form.owner_id = item.owner_id
        this.ownerSearch = item.label
        this.kwAutoResolvedType = 'owner'
      }
      if (item.zone_id) {
        this.form.zones = item.zone_id
        if (!this.zonesSelected.includes(item.zone_id)) this.zonesSelected.push(item.zone_id)
        this.kwAutoResolvedType = this.kwAutoResolvedType || 'zone'
      }
      if (item.transit_id) {
        const type = item.transit_type
        if (type === 'bts')          { this.form.bts_ids = item.transit_id; if (!this.btsSelected.includes(item.transit_id)) this.btsSelected.push(item.transit_id) }
        else if (type === 'mrt')     { this.form.mrt_ids = item.transit_id; if (!this.mrtSelected.includes(item.transit_id)) this.mrtSelected.push(item.transit_id) }
        else if (type === 'airport') { this.form.arl_ids = item.transit_id; if (!this.arlSelected.includes(item.transit_id)) this.arlSelected.push(item.transit_id) }
        this.kwAutoResolvedType = this.kwAutoResolvedType || 'transit'
      }
      this.kwSuggestions = []
      this.showKwSuggestions = false
      this.kwIndex = -1

      this.$nextTick(() => this.submitKeyword())
    },
    hideKwDropdown() {
      // Scrolling the suggestions list blurs the input to dismiss the keyboard,
      // but the list itself should stay visible while the user is browsing it.
      if (this.kwScrollBlur) { this.kwScrollBlur = false; return }
      setTimeout(() => { this.showKwSuggestions = false }, 160)
    },

    // ── Date ranges ──
    onExpireRangeUpdate({ startDate, endDate }) {
      if (startDate && endDate) {
        this.form.minDateExpire = moment(startDate).format('YYYY-MM-DD')
        this.form.maxDateExpire = moment(endDate).format('YYYY-MM-DD')
      }
    },
    onAvlRangeUpdate({ startDate, endDate }) {
      if (startDate && endDate) {
        this.form.minDateAvl = moment(startDate).format('YYYY-MM-DD')
        this.form.maxDateAvl = moment(endDate).format('YYYY-MM-DD')
      }
    },
    clearExpire() {
      this.expireRange = { startDate: null, endDate: null }
      this.form.minDateExpire = null
      this.form.maxDateExpire = null
    },
    clearAvl() {
      this.avlRange = { startDate: null, endDate: null }
      this.form.minDateAvl = null
      this.form.maxDateAvl = null
    },

    // ── Capsule chip clear ──
    clearChip(key) {
      if (key === 'template')          { this.reset(); return }
      if (key === '_toolbar_status')   { this.$emit('clear-toolbar-status'); return }
      if (key === '_toolbar_expiring') { this.$emit('clear-toolbar-expiring'); return }
      const CLEAR = {
        action_id:     () => { this.form.action_id = ''; this.actionSelected = []; this.appliedActionSelected = [] },
        property_type: () => { this.form.property_type = null; this.appliedPropertyTypeName = '' },
        bedrooms:      () => { this.form.bedrooms = ''; this.selectedBedrooms = [] },
        price:         () => { this.form.priceMin = null; this.form.priceMax = null; this.selectedPriceVal = '' },
        sqm:           () => { this.form.sqm_min = null; this.form.sqm_max = null; this.selectedSqmVal = '' },
        keyword:        () => { this.form.keyword = ''; this.form.propertyfilter = '' },
        propertyfilter: () => { this.form.propertyfilter = ''; this.form.place_id = null },
        place_id:      () => { this.form.place_id = null; this.placeSearch = '' },
        owner_id:      () => { this.form.owner_id = null; this.ownerSearch = '' },
        zones:         () => { this.form.zones = ''; this.zonesSelected = [] },
        bts_ids:       () => { this.form.bts_ids = ''; this.btsSelected = [] },
        mrt_ids:       () => { this.form.mrt_ids = ''; this.mrtSelected = [] },
        arl_ids:       () => { this.form.arl_ids = ''; this.arlSelected = [] },
        unit:          () => { this.form.unit = '' },
        floor:         () => { this.form.floor = '' },
        building:      () => { this.form.building = '' },
        type_name:     () => { this.form.type_name = '' },
        fac_ids:       () => { this.form.fac_ids = ''; this.facSelected = [] },
        amenity_ids:   () => { this.form.amenity_ids = ''; this.amenitySelected = [] },
        furniture_ids: () => { this.form.furniture_ids = ''; this.furnitureSelected = [] },
        expire:        () => { this.form.minDateExpire = null; this.form.maxDateExpire = null; this.expireRange = { startDate: null, endDate: null } },
        avl:           () => { this.form.minDateAvl = null; this.form.maxDateAvl = null; this.avlRange = { startDate: null, endDate: null } },
        rentPrice:     () => { this.form.rentPriceMin = null; this.form.rentPriceMax = null },
        salePrice:     () => { this.form.salePriceMin = null; this.form.salePriceMax = null },
      }
      if (CLEAR[key]) {
        CLEAR[key]()
        // Same keyword suppression as _applyFilter — otherwise clearing an unrelated
        // chip re-derives appliedForm straight from this.form and resurrects a
        // "Search" chip duplicating whatever field the keyword already resolved to.
        const payload = { ...this.form }
        if (this.kwAutoResolvedType) payload.keyword = ''
        this.appliedForm = payload
        this.appliedPlaceSearch = this.placeSearch
        this.appliedOwnerSearch = this.ownerSearch
        this.$emit('apply-filter', payload)
      }
    },

    // ── Submit / Reset ──
    _applyFilter(collapse = true) {
      const payload = { ...this.form }
      if (this.kwWasPasted && payload.keyword) {
        payload.propertyfilter = payload.keyword.trim()
        payload.keyword = ''
        this.kwWasPasted = false
      }
      // Once a keyword suggestion resolves to a specific field (building, place, owner, etc.)
      // keep suppressing the raw keyword on every submit — not just the first — until the
      // user actually edits the keyword box again (onKeywordInput resets the flag then).
      // Otherwise a later submit (e.g. from the advanced-filter panel) re-adds it and
      // duplicates the resolved field's chip.
      if (this.kwAutoResolvedType) {
        payload.keyword = ''
      }
      if (!payload.priceMin && !payload.priceMax) {
        const ids = payload.action_id ? payload.action_id.toString().split(',').map(Number) : []
        if (ids.includes(4) && (payload.rentPriceMin || payload.rentPriceMax)) {
          payload.priceMin = payload.rentPriceMin; payload.priceMax = payload.rentPriceMax
        } else if (ids.includes(3) && (payload.salePriceMin || payload.salePriceMax)) {
          payload.priceMin = payload.salePriceMin; payload.priceMax = payload.salePriceMax
        } else if (!ids.length) {
          if (payload.rentPriceMin || payload.rentPriceMax) {
            payload.priceMin = payload.rentPriceMin; payload.priceMax = payload.rentPriceMax
          } else if (payload.salePriceMin || payload.salePriceMax) {
            payload.priceMin = payload.salePriceMin; payload.priceMax = payload.salePriceMax
          }
        }
      }
      if (collapse) this.collapsed = true
      this.appliedForm = { ...payload }
      this.appliedPlaceSearch = this.placeSearch
      this.appliedOwnerSearch = this.ownerSearch
      this.appliedActionSelected = [...this.actionSelected]
      const pt = this.propertyTypes.find(o => o.id === payload.property_type)
      this.appliedPropertyTypeName = pt ? pt.name : ''
      const emitPayload = { ...payload }
      if (this.placeSearch) emitPayload.place_name = this.placeSearch
      if (this.ownerSearch) emitPayload.owner_name = this.ownerSearch
      this.$emit('apply-filter', emitPayload)
    },
    submit() { this._applyFilter(true) },
    debouncedSubmit() {
      clearTimeout(this.submitDebounceTimer)
      this.submitDebounceTimer = setTimeout(() => this._applyFilter(false), 600)
    },
    reset() {
      Object.assign(this.form, {
        keyword: '', propertyfilter: '', action_id: '', property_type: null,
        bedrooms: '', priceMin: null, priceMax: null, sqm_min: null, sqm_max: null,
        bts_ids: '', mrt_ids: '', arl_ids: '', zones: '',
        minDateExpire: null, maxDateExpire: null, minDateAvl: null, maxDateAvl: null,
        place_id: null, owner_id: null, unit: '', floor: '', type_name: '',
        building: '', fac_ids: '', amenity_ids: '', furniture_ids: '',
        rentPriceMin: null, rentPriceMax: null, salePriceMin: null, salePriceMax: null,
      })
      this.actionSelected = []; this.selectedBedrooms = []; this.bedroomInputVal = ''
      this.zonesSelected = []; this.btsSelected = []; this.mrtSelected = []; this.arlSelected = []
      this.facSelected = []; this.amenitySelected = []; this.furnitureSelected = []
      this.selectedPriceVal = ''; this.selectedSqmVal = ''
      this.placeSearch = ''; this.ownerSearch = ''
      this.appliedForm = null
      this.activeTemplate = null
      this.appliedPlaceSearch = ''; this.appliedOwnerSearch = ''
      this.appliedActionSelected = []; this.appliedPropertyTypeName = ''
      this.expireRange = { startDate: null, endDate: null }
      this.avlRange    = { startDate: null, endDate: null }
      this.$emit('clear-filter')
    },

    // ── Templates ──
    loadTemplate(t) {
      // Reset all form fields and related state first
      Object.assign(this.form, {
        keyword: '', propertyfilter: '', action_id: '', property_type: null,
        bedrooms: '', priceMin: null, priceMax: null, sqm_min: null, sqm_max: null,
        bts_ids: '', mrt_ids: '', arl_ids: '', zones: '',
        minDateExpire: null, maxDateExpire: null, minDateAvl: null, maxDateAvl: null,
        place_id: null, owner_id: null, unit: '', floor: '', type_name: '',
        building: '', fac_ids: '', amenity_ids: '', furniture_ids: '',
        rentPriceMin: null, rentPriceMax: null, salePriceMin: null, salePriceMax: null,
      })
      this.actionSelected = []; this.selectedBedrooms = []; this.bedroomInputVal = ''
      this.zonesSelected = []; this.btsSelected = []; this.mrtSelected = []; this.arlSelected = []
      this.facSelected = []; this.amenitySelected = []; this.furnitureSelected = []
      this.selectedPriceVal = ''; this.selectedSqmVal = ''
      this.placeSearch = ''; this.ownerSearch = ''
      this.appliedPlaceSearch = ''; this.appliedOwnerSearch = ''
      this.appliedActionSelected = []; this.appliedPropertyTypeName = ''
      this.expireRange = { startDate: null, endDate: null }
      this.avlRange    = { startDate: null, endDate: null }
      // Apply template values on top of clean state
      Object.keys(this.form).forEach(k => { if (t[k] !== undefined) this.form[k] = t[k] })
      // Sync derived display state (same as applyInitialFilter)
      if (t.action_id)      this.actionSelected      = t.action_id.toString().split(',').filter(Boolean)
      if (t.bedrooms)       this.selectedBedrooms    = t.bedrooms.toString().split(',').map(Number)
      if (t.zones)          this.zonesSelected       = t.zones.toString().split(',').map(Number)
      if (t.bts_ids)        this.btsSelected         = t.bts_ids.toString().split(',').map(Number)
      if (t.mrt_ids)        this.mrtSelected         = t.mrt_ids.toString().split(',').map(Number)
      if (t.arl_ids)        this.arlSelected         = t.arl_ids.toString().split(',').map(Number)
      if (t.fac_ids)        this.facSelected         = t.fac_ids.toString().split(',').map(Number)
      if (t.amenity_ids)    this.amenitySelected     = t.amenity_ids.toString().split(',').map(Number)
      if (t.furniture_ids)  this.furnitureSelected   = t.furniture_ids.toString().split(',').map(Number)
      if (t.minDateExpire && t.maxDateExpire) {
        this.expireRange = { startDate: moment(t.minDateExpire), endDate: moment(t.maxDateExpire) }
      }
      if (t.minDateAvl && t.maxDateAvl) {
        this.avlRange = { startDate: moment(t.minDateAvl), endDate: moment(t.maxDateAvl) }
      }
      this.activeTemplate = t
      this.appliedForm = { ...this.form }
      this.$nextTick(() => this.$emit('apply-filter', { ...this.form }))
    },
    async saveTemplate() {
      const result = await this.$swal({
        title: 'Save Filter Template',
        input: 'text',
        inputPlaceholder: 'Template name...',
        showCancelButton: true,
        confirmButtonText: 'Save',
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-danger ml-1' },
        buttonsStyling: false,
      })
      if (result.isConfirmed && result.value) {
        try {
          await axios.post('/property/v1/add_template', { ...this.form, title: result.value, created_by: this.userData.id })
          await this.loadTemplates()
          this.$toast({ component: ToastificationContent, props: { title: 'Template saved!', icon: 'CheckIcon', variant: 'success' } })
        } catch {
          this.$toast({ component: ToastificationContent, props: { title: 'Error saving template', icon: 'AlertTriangleIcon', variant: 'danger' } })
        }
      }
    },
    deleteTemplate(id) {
      this.$bvModal.msgBoxConfirm('Delete this template?', {
        title: 'Confirm', size: 'sm', okVariant: 'danger', okTitle: 'Yes', cancelTitle: 'No', centered: true,
      }).then(ok => {
        if (ok) axios.delete('/property/v1/delete_template/' + id).then(() => { this.loadTemplates() })
      })
    },

    // ── Data loaders ──
    async loadTypes() {
      try { const r = await axios.get('place/v1/type'); this.propertyTypes = [{ id: null, name: 'All' }, ...(r.data.data || []).sort((a, b) => a.seq - b.seq)] } catch {}
    },
    async loadZones() {
      try { const r = await axios.post('zone/v1/lists', { perpage: 2000, offset: 0 }); this.zones = r.data.data || [] } catch {}
    },
    async loadBts() {
      try { const r = await axios.get('transit/v1/listtype/bts'); this.btsList = r.data.data || [] } catch {}
    },
    async loadMrt() {
      try { const r = await axios.get('transit/v1/listtype/mrt'); this.mrtList = r.data.data || [] } catch {}
    },
    async loadArl() {
      try { const r = await axios.post('transit/v1/lists', { type_code: 'airport' }); this.arlList = r.data.data || [] } catch {}
    },
    async loadFacilities() {
      try { const r = await axios.post('facility/v1/lists', { perpage: 100, offset: 0, type_code: '1', is_active: 1 }); this.facList = r.data.data || [] } catch {}
    },
    async loadAmenities() {
      try { const r = await axios.post('facility/v1/lists', { perpage: 100, offset: 0, type_code: '2', is_active: 1 }); this.amenityList = r.data.data || [] } catch {}
    },
    async loadFurniture() {
      try { const r = await axios.get('furnituretype/v1/list'); this.furnitureTypes = r.data.data || [] } catch {}
    },
    async loadTemplates() {
      try { const r = await axios.get('/property/v1/get_templates/' + this.userData.id); this.templateData = r.data.data || [] } catch {}
    },
  },
}
</script>

<style scoped>
.filter-topbar { border-bottom: 1px solid #ebe9f1; }

/* Active filter capsules */
.capsule-row { display: flex; flex-wrap: wrap; align-items: center; background: transparent; }
.capsule-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.65rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background: #e6f4ea;
  color: #2d7a3a;
  border: 1px solid #a8d5b0;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
  gap: 0.2rem;
}
.capsule-tag:hover { background: #d0ecd6; }
.capsule-label { color: #5da867; font-weight: 400; margin-right: 0.2rem; }
.capsule-value { font-weight: 600; }
.capsule-x { opacity: 0.6; flex-shrink: 0; }
.capsule-clear { background: #fff0f0; color: #e63946; border-color: #f5c6cb; }
.capsule-clear:hover { background: #ffd6d6; }

.keyword-wrap { flex: 1; min-width: 200px; max-width: 360px; }

/* Desktop top bar fields */
.topbar-input-label { display: block; font-size: 0.72rem; font-weight: 600; color: #5e5873; margin-bottom: 0.25rem; white-space: nowrap; }
.topbar-field-code { min-width: 180px; flex: 2; }
.topbar-field-owner { min-width: 140px; flex: 1.5; }
.topbar-field-sm { width: 130px; flex-shrink: 0; }
.topbar-icon-btn { padding: 0.4rem 0.5rem !important; line-height: 1 !important; border: none !important; box-shadow: none !important; }
.topbar-clearable { padding-right: 1.4rem !important; }
.topbar-clear-btn { position: absolute; right: 0.45rem; top: 50%; transform: translateY(-50%); cursor: pointer; color: #6e6b7b; background: #d8d6de; border-radius: 50%; width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; z-index: 2; }
.topbar-clear-btn:hover { background: #ea5455; color: #fff; }

.field-label {
  display: block;
  font-size: 0.857rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
}

/* Action toggle */
.toggle-group { overflow: hidden; display: flex; }
.toggle-btn { flex: 1; border: none !important; box-shadow: none !important; border-radius: 0 !important; }

/* Bedroom chips */
.chip-row { display: flex; flex-wrap: wrap; gap: 0.35rem; }
.chip-btn {
  border-radius: 20px !important;
  padding: 0.5rem 1.25rem !important;
  font-size: 1rem;
  font-weight: 600;
  white-space: nowrap;
}

/* Price / SQM range group */
.range-group { border: 1px solid #d8d6de; border-radius: 0.357rem; }
.range-group .form-control { border: none !important; box-shadow: none !important; border-radius: 0 !important; }
.range-sep { border: none !important; background: transparent !important; }
.range-dropdown { position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 9999; }

/* Facility checkbox grid */
.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.5rem;
  padding: 0.5rem 0;
}
@media (max-width: 768px) { .checkbox-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 480px) { .checkbox-grid { grid-template-columns: repeat(2, 1fr); } }

/* Keyword suggestion badges */
.kw-badge { display: inline-block; padding: 0.1rem 0.4rem; border-radius: 0.2rem; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; flex-shrink: 0; }
.kw-code      { background: #e8f4fd; color: #1a73e8; }
.kw-place     { background: #e6f4ea; color: #1e8e3e; }
.kw-owner     { background: #fce8e6; color: #d93025; }
.kw-building  { background: #fef7e0; color: #f29900; }
.kw-zone      { background: #f3e8ff; color: #7b2ff7; }
.kw-transit   { background: #e8fff3; color: #00875a; }
.kw-unit_type { background: #fff0f6; color: #c2255c; }
.kw-floor     { background: #e8f0fe; color: #1967d2; }
.kw-unit_no   { background: #fdf3e7; color: #d46b08; }
.kw-house_no  { background: #e6f7ff; color: #0958d9; }

@media (max-width: 575px) {
  .filter-toggle-btn { border: none !important; background: transparent !important; box-shadow: none !important; padding-left: 0.25rem; padding-right: 0.25rem; }
}
@media (min-width: 576px) {
  .w-sm-auto { width: auto !important; }
  .flex-sm-grow-0 { flex-grow: 0 !important; }
}

.zindex-dropdown { z-index: 1050; }
.list-group-item.active { background-color: #80AC03; color: #fff; }

.kw-clear-wrap { background: #fff; border-left: none !important; border-right: none !important; padding: 0 6px; cursor: pointer; }
.kw-clear-circle { background: #d8d6de; border-radius: 50%; width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; color: #6e6b7b; flex-shrink: 0; }
.kw-clear-circle:hover { background: #ea5455; color: #fff; }

.reportrange-text { padding: 0.438rem 1rem !important; border: 1px solid #d8d6de !important; border-radius: 0.357rem !important; }

/* Mobile search trigger (opens the full-screen keyword sheet) */
.search-trigger {
  color: #6e6b7b;
  text-align: left;
  overflow: hidden;
}
.search-trigger span { min-width: 0; }

/* Keyword sheet: mobile-only full-screen overlay (advanced filters stay an inline b-collapse) */
.keyword-sheet {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1060;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.sheet-header-mobile {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid #ebe9f1;
  flex-shrink: 0;
}
.sheet-back-btn {
  background: transparent;
  border: none;
  padding: 0.25rem;
  flex-shrink: 0;
  color: #5e5873;
  line-height: 1;
}
.keyword-sheet-body {
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: env(safe-area-inset-bottom);
}
.sheet-kw-suggestions .list-group-item { padding-top: 0.6rem; padding-bottom: 0.6rem; }

/* Slide up from the bottom */
.keyword-sheet-anim-enter-active,
.keyword-sheet-anim-leave-active { transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1); }
.keyword-sheet-anim-enter,
.keyword-sheet-anim-leave-to { transform: translateY(100%); }
</style>
